import { types, getParentOfType, getParent, getRoot, flow } from 'mobx-state-tree';
import UIStore from '../../stores/UIStore';
import Image from './Image';
import FailedValidation from './FailedValidation';
import PricingStrategy from './PricingStrategy';
import ItemCategory from './ItemCategory';
import ItemLabel from './ItemLabel';
import ItemSpecific from './ItemSpecific';
import Variation from './Variation';
import { deserialize } from '../../components/common/HTMLEditor/serializerRules';

const regex = /&lt;([\s\S]*?)&gt;/gs;

const Item = types
  .model('Item', {
    // from response
    accountCode: types.optional(types.string, ''),
    categories: types.maybe(types.string),
    conditionId: types.maybe(types.number),
    description: types.maybe(types.string),
    discoveryId: types.maybe(types.number),
    failedValidations: types.maybe(types.array(FailedValidation), []),
    finishDatetime: types.maybe(types.string),
    htmlTemplateSystemId: types.optional(types.string, '12345678'),
    id: types.maybe(types.number),
    images: types.optional(types.array(Image), []),
    itemCategories: types.optional(types.array(ItemCategory), []),
    itemLabels: types.optional(types.array(ItemLabel), []),
    itemSpecifics: types.optional(types.array(ItemSpecific), []),
    mainImageUrl: types.maybe(types.string),
    pricingStrategy: types.optional(PricingStrategy, {}),
    selectedBusinessPolicyId: types.maybe(types.string),
    selectedDestinationCategoryId: types.maybe(types.number),
    sourceItemId: types.optional(types.string, ''),
    sourceMarketplaceId: types.optional(types.string, ''),
    sourceRank: types.maybe(types.number),
    startDatetime: types.optional(types.string, ''),
    statusDetails: types.optional(types.string, ''),
    statusId: types.optional(types.number, 0),
    title: types.maybe(types.string),
    titleFailedWords: types.maybe(types.array(types.string)),
    brandFailedWords: types.optional(types.array(types.string), []),
    descriptionFailedWords: types.optional(types.array(types.string), []),
    variations: types.optional(types.array(Variation), []),
    isOptimized: types.maybe(types.boolean),
    amazonListingFlow: types.maybe(types.number),
  })
  .views(self => ({
    get salePrice() {
      return self.pricingStrategy.salePrice || '';
    },
    get profitValue() {
      return self.pricingStrategy.profitValue || '';
    },
    get profitPercent() {
      return self.pricingStrategy.profitPercent || '';
    },
    get quantity() {
      return self.pricingStrategy.quantity || '';
    },
    get breakEven() {
      return self.pricingStrategy.breakEven || '';
    },
    get cleanedDescription() {
      if (regex.test(self.description) || /&nbsp;/g.test(self.description)) {
        return self.description
          .replace('&nbsp;', ' ')
          .replace(regex, '')
          .replace(/<img[^>]*>/g,"")
          .trim();
      }
      return self.description.replace(/<img[^>]*>/g,"").trim();
    },
    get deserializedDescription() {
      const parsed = new DOMParser().parseFromString(self.cleanedDescription, 'text/html');
      return deserialize(parsed.body);
    },
    get enums() {
      return getParentOfType(self, UIStore).enumsStore.getEnumsList;
    },
    get codeType() {
      switch (self.enums.NEW_DISCOVERY_SOURCE_NAMES[self.sourceMarketplaceId]) {
        case self.enums.SOURCE_MARKETPLACE.AMAZON_US: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_UK: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_CA: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_DE: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_IT: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_FR: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.AMAZON_ES: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.WALMART: {
          return self.enums.CODES.MARKET_ID;
        }
        case self.enums.SOURCE_MARKETPLACE.CJ_US: {
          return self.enums.CODES.ASIN;
        }
        case self.enums.SOURCE_MARKETPLACE.CJ_CN: {
          return self.enums.CODES.ASIN;
        }
        default: {
          return null;
        }
      }
    },
    get isVariationsIncluded() {
      return getParent(self, 2).includesVariants;
    },
  }))
  .actions(self => {
    const setId = id => {
      self.id = id;
    };
    const setTitle = title => {
      self.title = title;
    };
    const setTargetPrice = destinationPrice => {
      self.destinationPrice = destinationPrice;
    };
    const setDescription = description => {
      self.description = description;
    };
    const setTemplate = template => {
      self.template = template;
    };

    const increaseQuantity = () => {
      self.pricingStrategy.increaseQuantity();
    };

    const decreaseQuantity = () => {
      self.pricingStrategy.decreaseQuantity();
    };

    const setNewValue = (property, value) => {
      self[property] = value;
    };

    const setProfitType = profitType => {
      self.pricingStrategy.profitType = profitType;
    };

    const setVariationsIncluded = bool => {
      getParent(self, 2).setIncludesVariants(bool);
    };
    const setMainImage = imgUrl => {
      self.mainImage = imgUrl;
    };
    const removeSpecific = id => {
      self.itemSpecifics = self.itemSpecifics.filter(spec => spec.id !== id);
    };
    const addSpecific = newSpec => {
      const newSpecific = ItemSpecific.create(newSpec);
      self.itemSpecifics.push(newSpecific);
    };
    const updateSpecifics = flow(function*(itemSpec) {
      try {
        const resp = yield getRoot(self).uiStore.discoveryStore.addItemSpecific({
          discoveryItemId: self.id,
          ...itemSpec,
        });
        self.addSpecific(resp);
      } catch (err) {
        console.error(err);
      }
    });
    return {
      addSpecific,
      updateSpecifics,
      setTitle,
      setDescription,
      setTemplate,
      setId,
      setTargetPrice,
      increaseQuantity,
      decreaseQuantity,
      setNewValue,
      setProfitType,
      setVariationsIncluded,
      setMainImage,
      removeSpecific,
    };
  });

export default Item;
